import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, Lock, ShieldCheck, Sparkles } from "lucide-react";
import { Seal } from "./Logo";
import { openChat } from "../../lib/api";
import { useContent, pick } from "../../lib/content";

const ease = [0.22, 1, 0.36, 1];

const Line = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className={`block ${className}`} initial={{ y: "110%" }} animate={{ y: "0%" }}
      transition={{ duration: 1.2, delay, ease }}>
      {children}
    </motion.span>
  </span>
);

const badges = [
  [Sparkles, "Résultat Garanti à 100%", "hero-badge-guarantee"],
  [Lock, "Travail en toute discrétion", "hero-badge-secure"],
  [ShieldCheck, "Si vous n'avez pas de résultat, ne payez pas !", "hero-badge-nopay"],
];

export const Hero = () => {
  const c = useContent();
  const subLines = pick(c, "hero_subtitle",
    "Grand Spécialiste des Pratiques Mystiques,\nRetour d'Affection & Succès Professionnel").split("\n").slice(0, 3);
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 60, damping: 15 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 60, damping: 15 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden pt-28 md:pt-32">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_70%_30%,rgba(212,175,55,0.14),transparent_60%)]" />
      <div className="relative mx-auto grid max-w-7xl gap-12 px-5 pb-20 md:px-10 lg:grid-cols-12 lg:gap-6">
        <motion.div style={{ y: textY }} className="relative z-10 lg:col-span-7 lg:pt-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }}
            className="mb-6 flex items-center gap-3 font-mono-x text-xs uppercase tracking-[0.3em] text-amber-400">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            En consultation · Chat instantané
          </motion.p>
          <h1 className="font-display text-[15vw] leading-[0.92] text-amber-50 sm:text-7xl lg:text-[7.4rem]" data-testid="hero-title">
            <Line delay={0.35}>Maître</Line>
            <Line delay={0.5} className="gold-text">Wouffa</Line>
          </h1>
          <div className="mt-6 max-w-xl">
            {subLines.map((l, i) => (
              <Line key={i} delay={0.7 + 0.12 * i} className="font-serif-it text-2xl italic leading-snug text-stone-200 md:text-3xl">
                {l}
              </Line>
            ))}
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9, ease }}
            className="mt-10 flex flex-wrap gap-2">
            {badges.map(([Icon, t, id]) => (
              <span key={id} data-testid={id}
                className="flex items-center gap-2 rounded-full border border-amber-700/40 bg-amber-500/10 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-amber-200">
                <Icon className="h-3.5 w-3.5 text-amber-400" /> {t}
              </span>
            ))}
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.9, ease }}
            className="mt-10 flex flex-wrap items-center gap-6">
            <button onClick={() => openChat()} data-testid="hero-cta-chat"
              className="group relative overflow-hidden rounded-full bg-amber-400 px-8 py-4 text-sm font-bold uppercase tracking-[0.18em] text-stone-950 transition-transform duration-300 hover:scale-[1.03] active:scale-95">
              <span className="relative z-10">Écrire au Maître</span>
              <span className="absolute inset-0 -translate-x-full bg-amber-100 transition-transform duration-500 group-hover:translate-x-0" />
            </button>
            <a href="#amour" data-testid="hero-cta-discover"
              onClick={(e) => { e.preventDefault(); window.__lenis?.scrollTo("#amour", { offset: -80 }); }}
              className="flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-stone-300 transition-colors hover:text-amber-300">
              Découvrir les rituels <ArrowDownRight className="h-4 w-4" />
            </a>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5" style={{ perspective: 1200 }}>
          <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.6, delay: 0.4, ease }}
            className="relative mx-auto aspect-[4/5] w-full max-w-md">
            <div className="absolute -inset-10 spin-slow" style={{ transform: "translateZ(-60px)" }}>
              <Seal className="h-full w-full" />
            </div>
            <div className="arch absolute inset-6 overflow-hidden border border-amber-500/40 shadow-[0_40px_120px_rgba(212,175,55,0.18)]"
              style={{ transform: "translateZ(40px)" }}>
              <motion.img style={{ y: imgY, scale: 1.2 }} src={pick(c, "hero_img", "/img/candles2.jpg")} alt="Bougies rituelles allumées"
                className="h-full w-full object-cover" data-testid="hero-image" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,transparent_25%,rgba(10,9,8,0.85)_80%)]" />
            </div>
            <div className="absolute -bottom-2 left-0 rounded-2xl border border-amber-500/30 bg-[#12100e]/90 px-5 py-4 backdrop-blur-md"
              style={{ transform: "translateZ(90px)" }} data-testid="hero-price-chip">
              <p className="font-mono-x text-[11px] uppercase tracking-[0.25em] text-amber-400">Tarif unique</p>
              <p className="font-display text-2xl text-amber-50">150€</p>
              <p className="text-xs text-stone-300">après satisfaction totale</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
